/**
 * schema.js — constants, enumerations, and alias helpers for the System of Work domain.
 * Pure: no imports, no state, no React. Aliases follow the SoW path namespace (single root session).
 */

// Cell status vocabulary (lifecycle order); `inactive` = user pause (heartbeat skips draft+inactive).
export const STATUSES = [
  'draft', 'planned', 'ready', 'working', 'input_needed', 'inactive', 'completed', 'failed'
];
export const TERMINAL_STATUSES = ['completed', 'failed'];
export const NONTERMINAL_STATUSES = ['draft', 'planned', 'ready', 'working', 'input_needed', 'inactive'];

export function statusPillClass(status) {
  if (status === 'failed' || status === 'input_needed') return 'title-tag-badge-warning';
  if (status === 'inactive') return 'title-tag-badge-muted';
  return 'title-tag-badge';
}

// Two step types; each yields { content, label }. workspace_init is gone (workspace is a cell property).
export const STEP_TYPES = ['worker_task', 'external_input'];
// The input_needed request kind (surfaced where input is shown — NOT on the step editor).
export const REQUEST_KINDS = ['approval', 'choice', 'question', 'review'];
export const AGENT_TYPES = ['claude_code', 'codex'];
export const PERMISSION_MODES = ['approval', 'autonomous'];
// Carryover forms selectable in the editor (advanced { last:N } / { step, runs:all } handled separately).
export const CARRYOVER_MODES = ['none', 'last', 'all', 'steps'];

// Workspaces are ACTUAL (not templates): { name, type: local_existing, work_folder }.
export const WORKSPACE_TYPES = ['local_existing'];
export const WORKSPACE_MODES = ['inherit_from_mission', 'specify_workspace', 'inherit_from_previous_cell'];
// Cell-workflow selection mode — parallels workspace_mode. "specify_workflow" picks a workflow now;
// both inherit modes resolve at runtime (from the mission default / the previous cell).
export const CELL_WORKFLOW_MODES = ['inherit_from_mission', 'specify_workflow', 'inherit_from_previous_cell'];

// Well-known session-global config objects.
export const CELL_WORKFLOWS_ALIAS = 'system/cell_workflows';
export const WORKSPACES_ALIAS = 'system/workspaces';
export const HEARTBEAT_STATE_ALIAS = 'system/heartbeat_state';

// ---- Alias helpers (path namespace) ----
export function missionAlias(id) {
  return `missions/${id}/graph`;
}
export function isMissionAlias(alias) {
  return typeof alias === 'string' && /^missions\/[^/]+\/graph$/.test(alias);
}
export function missionIdFromAlias(alias) {
  const m = typeof alias === 'string' && alias.match(/^missions\/([^/]+)\/graph$/);
  return m ? m[1] : null;
}
export function cellMetaThreadAlias(missionId, cellId) {
  return `missions/${missionId}/cells/${cellId}/meta`;
}
export function stepThreadAlias(missionId, cellId, step, run) {
  return `missions/${missionId}/cells/${cellId}/steps/${step}/${run}`;
}

// ---- Id minting (conform to the spec patterns) ----
const B36 = 'abcdefghijklmnopqrstuvwxyz0123456789';
function randToken(len) {
  let s = '';
  for (let i = 0; i < len; i++) s += B36[Math.floor(Math.random() * B36.length)];
  return s;
}
export function mintMissionId() {           // ^[a-z0-9]{6,8}$
  return randToken(6);
}
export function mintCellId() {              // ^[a-z0-9-]{4,12}$
  return randToken(8);
}

// ---- Default value helpers ----
export function emptyAgent() {
  return { type: '', model: '', effort: '', permissions: '', executable: '' };
}
